import { getDistanceInMeters } from '@Sage/ssm-utils';
import Axios from 'axios';

import { NEW_API_URL } from 'constants/Environment';
import { axios } from './axios';
import {
    PLACE_ACCOUNT_FORM_PARAMS_KEYS,
    PLACE_ENRICHED_ITEM_KEYS,
    PLACE_PREVIEW_KEYS,
    pickString,
} from './mapsLeadExtractionOpenApiFields';
import { handleErrors } from './utils';

const PREVIEW_ENDPOINT = '/analyzer/v1/workflows/nearby_places';
const ENRICH_ENDPOINT = '/analyzer/v1/workflows/place_to_account_form';
const BUSINESS_TYPES_ENDPOINT = '/analyzer/v1/mapsleadextraction/business-types';
const CREDITS_ENDPOINT = '/analyzer/v1/mapsleadextraction/credits';

export const ENRICH_SSE_FORMAT = 'sse';

/** SSE `data:` JSON uses `{ type: 'workflow.result', result: { entity, params, type: 'form' } }`. */
export const WORKFLOW_SSE_RESULT_TYPE = 'workflow.result';

const CANCELLED_REQUEST = { isCanceled: true };
const activeRequestSources = new Set();
const activeAbortControllers = new Set();

export const isCanceledRequest = (error) =>
    Boolean(error?.isCanceled || Axios.isCancel(error) || error?.message === 'cancel');

const createCancelConfig = () => {
    const source = Axios.CancelToken.source();
    activeRequestSources.add(source);

    return {
        cancelToken: source.token,
        source,
    };
};

const removeCancelSource = (source) => {
    activeRequestSources.delete(source);
};

export function cancelRequests() {
    activeRequestSources.forEach((source) => {
        source.cancel('cancel');
    });
    activeRequestSources.clear();
    activeAbortControllers.forEach((controller) => {
        controller.abort('cancel');
    });
    activeAbortControllers.clear();
}

const handleRequestError = (reject) => (error) => {
    if (isCanceledRequest(error)) {
        reject(CANCELLED_REQUEST);
        return;
    }

    handleErrors(reject)(error);
};

const toNumber = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
    const number = Number(normalized);
    return Number.isFinite(number) ? number : null;
};

const toNonNegativeInt = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
};

const unwrapNearbyPlacesPayload = (response) => {
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
        return response;
    }
    if (
        response.type === WORKFLOW_SSE_RESULT_TYPE &&
        response.result &&
        typeof response.result === 'object' &&
        !Array.isArray(response.result)
    ) {
        return response.result;
    }
    return response;
};

const getPreviewItems = (response) => {
    const payload = unwrapNearbyPlacesPayload(response);
    if (Array.isArray(payload?.result)) return payload.result;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.leads)) return payload.leads;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

/** Distance in kilometres (API / computed); always formatted as km, never metres. */
const formatDistanceKm = (valueKm) => {
    const km = toNumber(valueKm);
    if (km === null) return '';
    if (km < 10) return `${km.toFixed(2)} km`;
    if (km < 100) return `${km.toFixed(1)} km`;
    return `${Math.round(km)} km`;
};

const pickNumber = (record, keys) => toNumber(pickString(record, keys));

const getDistanceKm = ({ lat, lng, origin, rawDist }) => {
    const originLat = origin != null ? toNumber(origin.lat) : null;
    const originLng = origin != null ? toNumber(origin.lng) : null;
    if (originLat !== null && originLng !== null) {
        const meters = getDistanceInMeters(originLat, originLng, lat, lng);
        if (meters != null) return meters / 1000;
    }

    return toNumber(rawDist);
};

const getPreviewLeadId = ({ id, lat, lng, name }) => id || `${name}-${lat}-${lng}`;

export const normalizePreviewLead = (lead, origin = null) => {
    const backendId = pickString(lead, PLACE_PREVIEW_KEYS.id);
    const name = pickString(lead, PLACE_PREVIEW_KEYS.name);
    const lat = pickNumber(lead, PLACE_PREVIEW_KEYS.lat);
    const lng = pickNumber(lead, PLACE_PREVIEW_KEYS.lng);

    if (!name || lat === null || lng === null) return null;

    const id = getPreviewLeadId({ id: backendId, lat, lng, name });
    const category = pickString(lead, PLACE_PREVIEW_KEYS.category);
    const rawDist = pickString(lead, PLACE_PREVIEW_KEYS.dist);
    const distanceKm = getDistanceKm({ lat, lng, origin, rawDist });
    const distance = distanceKm !== null ? distanceKm : '';
    const distanceLabel = distanceKm !== null ? formatDistanceKm(distanceKm) : '';
    const street = pickString(lead, PLACE_PREVIEW_KEYS.street);
    const phone = pickString(lead, PLACE_PREVIEW_KEYS.phone);
    const website = pickString(lead, PLACE_PREVIEW_KEYS.website);
    const city = pickString(lead, PLACE_PREVIEW_KEYS.city);
    const postal = pickString(lead, PLACE_PREVIEW_KEYS.postal);
    const country = pickString(lead, PLACE_PREVIEW_KEYS.country);
    const email = pickString(lead, PLACE_PREVIEW_KEYS.email);
    const dataQualityScore = pickNumber(lead, PLACE_PREVIEW_KEYS.dataQualityScore);

    return {
        id,
        name,
        category,
        distance,
        distanceLabel,
        phone,
        email,
        website,
        street,
        city,
        postal,
        country,
        dataQualityScore,
        potential_duplicate: Boolean(lead?.[PLACE_PREVIEW_KEYS.potentialDuplicate]),
        position: { lat, lng },
        sourcePayload: lead,
        origin,
    };
};

export const normalizePreviewResponse = (response, origin) =>
    getPreviewItems(response)
        .map((lead) => normalizePreviewLead(lead, origin))
        .filter(Boolean);

export const parseNearbyPlacesResponse = (response, origin) => {
    const payload = unwrapNearbyPlacesPayload(response);
    const leads = normalizePreviewResponse(payload, origin);
    const paginated = payload && typeof payload === 'object' && !Array.isArray(payload);

    return {
        leads,
        hasMore: paginated ? Boolean(payload.hasMore) : false,
        nextPageQueryId: paginated ? String(payload.nextPageQueryId ?? '') : '',
        page: paginated ? toNonNegativeInt(payload.page) : 0,
        count: paginated ? toNonNegativeInt(payload.count) : leads.length,
        pageQueryId: paginated ? String(payload.pageQueryId ?? '') : '',
    };
};

export const normalizeBusinessTypes = (catalogue) => {
    if (!Array.isArray(catalogue) || !catalogue.length) return [];

    const toOption = (item) => {
        const value = String(item?.id ?? item?.value ?? '');
        const label = String(item?.name ?? item?.label ?? value);
        return value && label ? { label, value } : null;
    };

    const options = catalogue.some((item) => Array.isArray(item?.options))
        ? catalogue.flatMap((section) => (section.options || []).map(toOption))
        : catalogue.map(toOption);

    const seen = new Set();
    return options.filter((opt) => {
        if (!opt || seen.has(opt.value)) return false;
        seen.add(opt.value);
        return true;
    });
};

const isAccountFormEntry = (entry) =>
    entry?.[PLACE_ENRICHED_ITEM_KEYS.entity] === PLACE_ENRICHED_ITEM_KEYS.entityAccounts &&
    entry?.[PLACE_ENRICHED_ITEM_KEYS.type] === PLACE_ENRICHED_ITEM_KEYS.typeForm &&
    entry?.[PLACE_ENRICHED_ITEM_KEYS.params] &&
    typeof entry[PLACE_ENRICHED_ITEM_KEYS.params] === 'object' &&
    !Array.isArray(entry[PLACE_ENRICHED_ITEM_KEYS.params]);

/**
 * Account form entries from SSE arrive wrapped in a workflow envelope; batch/JSON paths use the inner shape.
 * @param {unknown} payload
 * @returns {unknown}
 */
export const unwrapWorkflowSsePayload = (payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return payload;
    }
    if (
        payload.type === WORKFLOW_SSE_RESULT_TYPE &&
        payload.result &&
        typeof payload.result === 'object' &&
        !Array.isArray(payload.result)
    ) {
        return payload.result;
    }
    return payload;
};

const getAccountFormEntry = (item) => {
    const entry = unwrapWorkflowSsePayload(item);
    if (isAccountFormEntry(entry)) return entry;
    if (!Array.isArray(entry)) return null;
    return entry.find(isAccountFormEntry) || null;
};

export const normalizeEnrichedItem = (item, sourceLead) => {
    const id = String(sourceLead?.id ?? '');
    const entry = unwrapWorkflowSsePayload(item);
    const formEntry = getAccountFormEntry(item);
    const formParams = formEntry?.[PLACE_ENRICHED_ITEM_KEYS.params] ?? null;

    const failed = !formParams || Boolean(entry?.[PLACE_ENRICHED_ITEM_KEYS.enhancementFailed]);

    if (failed) {
        return {
            id,
            sourceLead,
            status: 'failed',
            error: pickString(entry, PLACE_ENRICHED_ITEM_KEYS.enhancementError),
        };
    }

    const sourcePayload = sourceLead?.sourcePayload;
    const PARAMS = PLACE_ACCOUNT_FORM_PARAMS_KEYS;
    const scoreLead = pickNumber(formEntry, PLACE_ENRICHED_ITEM_KEYS.scoreLead);

    return {
        id,
        form: formParams,
        sourceLead,
        status: 'pending',
        data: {
            id,
            name: pickString(formParams, PARAMS.name),
            phone: pickString(formParams, PARAMS.phone),
            email: pickString(formParams, PARAMS.email),
            website: pickString(formParams, PARAMS.web),
            category: pickString(formParams, PARAMS.category),
            comment: pickString(formParams, PARAMS.comment),
            warningText: pickString(formParams, PARAMS.warningText),
            address1: pickString(formParams, PARAMS.address1),
            street: pickString(formParams, PARAMS.address1),
            address2: pickString(formParams, PARAMS.address2),
            city: pickString(formParams, PARAMS.city),
            province: pickString(formParams, PARAMS.region),
            postal: pickString(formParams, PARAMS.postcode),
            country: pickString(formParams, PARAMS.country),
            idCountryRaw: undefined,
            vatNumber: pickString(formParams, PARAMS.vatNumber),
            scoreLead,
            lat: pickNumber(sourcePayload, PLACE_PREVIEW_KEYS.lat),
            lng: pickNumber(sourcePayload, PLACE_PREVIEW_KEYS.lng),
            potential_duplicate: Boolean(sourceLead?.potential_duplicate),
        },
    };
};

const getEnrichmentItemForLead = (response, index, total) => {
    if (!Array.isArray(response)) return null;
    if (total === 1 && response.some(isAccountFormEntry)) return response;

    const item = response[index];
    if (Array.isArray(item)) return item;
    if (isAccountFormEntry(item)) return item;
    if (item?.[PLACE_ENRICHED_ITEM_KEYS.enhancementFailed]) return item;
    return null;
};

export const normalizeEnrichmentResponse = (response, sourceLeads = []) => {
    return sourceLeads.map((sourceLead, index) => {
        const item = getEnrichmentItemForLead(response, index, sourceLeads.length) || {
            [PLACE_ENRICHED_ITEM_KEYS.enhancementFailed]: true,
        };
        return normalizeEnrichedItem(item, sourceLead);
    });
};

/** Same shape rules as preview: array root, or `results` / `leads` on an object. */
const extractEnrichmentResponseArray = (data) => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
        if (Array.isArray(data.results)) return data.results;
        if (Array.isArray(data.leads)) return data.leads;
    }
    return null;
};

const getAuthorizationHeader = () => {
    const auth = axios.defaults.headers.common.Authorization;
    return typeof auth === 'string' ? auth : '';
};

const buildAnalyzerUrl = (path, searchParams) => {
    const baseUrl = String(NEW_API_URL || '').replace(/\/$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${baseUrl}${normalizedPath}`);
    if (searchParams) {
        Object.entries(searchParams).forEach(([key, value]) => {
            if (value != null) url.searchParams.set(key, String(value));
        });
    }
    return url.toString();
};

export function nearbyPlaces(payload, origin) {
    const { cancelToken, source } = createCancelConfig();

    return new Promise((resolve, reject) => {
        axios
            .post(PREVIEW_ENDPOINT, payload, { cancelToken })
            .then(({ data }) => resolve(parseNearbyPlacesResponse(data, origin)))
            .catch(handleRequestError(reject))
            .finally(() => removeCancelSource(source));
    });
}

export function getBusinessTypes() {
    const { cancelToken, source } = createCancelConfig();

    return new Promise((resolve, reject) => {
        axios
            .get(BUSINESS_TYPES_ENDPOINT, { cancelToken })
            .then(({ data }) => resolve(normalizeBusinessTypes(data)))
            .catch(handleRequestError(reject))
            .finally(() => removeCancelSource(source));
    });
}

export function getCredits() {
    const { cancelToken, source } = createCancelConfig();

    return new Promise((resolve, reject) => {
        axios
            .get(CREDITS_ENDPOINT, { cancelToken })
            .then(({ data }) =>
                resolve({
                    balance: toNonNegativeInt(data?.balance),
                    spent: toNonNegativeInt(data?.spent),
                }),
            )
            .catch(handleRequestError(reject))
            .finally(() => removeCancelSource(source));
    });
}

export function placeToAccountForm(leads) {
    const sourceLeads = leads || [];
    if (!sourceLeads.length) {
        return Promise.resolve([]);
    }

    const payload = sourceLeads.map((lead) => lead.sourcePayload);
    const { cancelToken, source } = createCancelConfig();

    return new Promise((resolve, reject) => {
        axios
            .post(ENRICH_ENDPOINT, payload, { cancelToken })
            .then(({ data }) => {
                const arr = extractEnrichmentResponseArray(data);
                resolve(normalizeEnrichmentResponse(arr ?? [], sourceLeads));
            })
            .catch(handleRequestError(reject))
            .finally(() => removeCancelSource(source));
    });
}

/**
 * If the server returns a plain JSON batch (same shape as `?format=web`) instead of SSE,
 * parse it and return normalized rows. Returns null when the body is not a usable JSON array.
 */
const tryInterpretAsJsonEnrichmentBody = (rawText, sourceLeads) => {
    const t = String(rawText ?? '')
        .trim()
        .replace(/^\uFEFF/, '');
    if (!t || t === '[DONE]') return null;
    if (!t.startsWith('[') && !t.startsWith('{')) return null;
    let data;
    try {
        data = JSON.parse(t);
    } catch {
        return null;
    }
    if (Array.isArray(data)) {
        return normalizeEnrichmentResponse(data, sourceLeads);
    }
    const nested = extractEnrichmentResponseArray(data);
    if (nested) {
        return normalizeEnrichmentResponse(nested, sourceLeads);
    }
    if (sourceLeads.length === 1 && data && typeof data === 'object' && !Array.isArray(data)) {
        return normalizeEnrichmentResponse([data], sourceLeads);
    }
    return null;
};

/**
 * Enrichment via server stream when supported (`?format=sse`, `Accept: text/event-stream`).
 * Falls back to the same JSON batch contract as `placeToAccountForm` when the response is
 * `application/json` or the body is a single JSON array (some gateways ignore SSE headers).
 */
export function placeToAccountFormStream(leads, { onItem, onProgress } = {}) {
    const sourceLeads = leads || [];
    if (!sourceLeads.length) {
        return Promise.resolve([]);
    }

    const controller = new AbortController();
    activeAbortControllers.add(controller);

    const payload = sourceLeads.map((lead) => lead.sourcePayload);
    const url = buildAnalyzerUrl(ENRICH_ENDPOINT, { format: ENRICH_SSE_FORMAT });
    const authorization = getAuthorizationHeader();

    const emitBatch = (normalized) => {
        normalized.forEach((item, idx) => {
            onItem?.(item, idx);
            onProgress?.({ current: idx + 1, total: sourceLeads.length });
        });
        return normalized;
    };

    const results = [];

    return (async () => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: authorization,
                    Accept: 'text/event-stream',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            if (!response.ok) {
                const err = new Error(`HTTP ${response.status}`);
                err.status = response.status;
                throw err;
            }

            const contentType = (response.headers.get('content-type') || '').toLowerCase();

            if (contentType.includes('application/json')) {
                const data = await response.json();
                const arr = extractEnrichmentResponseArray(data);
                const normalized = normalizeEnrichmentResponse(arr ?? [], sourceLeads);
                return emitBatch(normalized);
            }

            if (!response.body) {
                throw new Error('No response body');
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let index = 0;

            const applyEventData = (jsonStr) => {
                if (!jsonStr || jsonStr === '[DONE]') {
                    return;
                }
                let parsed;
                try {
                    parsed = JSON.parse(jsonStr);
                } catch {
                    const parseErr = new Error('Invalid SSE event payload');
                    if (results.length > 0) {
                        parseErr.partial = results;
                    }
                    throw parseErr;
                }
                const batch = Array.isArray(parsed)
                    ? parsed
                    : extractEnrichmentResponseArray(parsed);
                if (batch != null) {
                    for (let i = 0; i < batch.length; i += 1) {
                        const sourceLead = sourceLeads[index];
                        if (!sourceLead) return;
                        const normalized = normalizeEnrichedItem(batch[i], sourceLead);
                        results.push(normalized);
                        onItem?.(normalized, index);
                        onProgress?.({ current: index + 1, total: sourceLeads.length });
                        index += 1;
                    }
                    return;
                }
                const sourceLead = sourceLeads[index];
                if (!sourceLead) return;
                const normalized = normalizeEnrichedItem(parsed, sourceLead);
                results.push(normalized);
                onItem?.(normalized, index);
                onProgress?.({ current: index + 1, total: sourceLeads.length });
                index += 1;
            };

            /** One `data:` line = one JSON object (do not join multiple lines — that breaks NDJSON-style chunks). */
            const flushEventBlock = (block) => {
                const lines = block.split(/\r?\n/);
                for (const line of lines) {
                    if (!line.startsWith('data:')) continue;
                    const jsonStr = line.slice(5).trim();
                    if (jsonStr) applyEventData(jsonStr);
                }
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                const chunkText = decoder.decode(value, { stream: true });
                buffer += chunkText;
                buffer = buffer.replace(/\r\n/g, '\n');

                let boundary;
                while ((boundary = buffer.indexOf('\n\n')) !== -1) {
                    const rawEvent = buffer.slice(0, boundary);
                    buffer = buffer.slice(boundary + 2);
                    flushEventBlock(rawEvent);
                }
            }

            if (buffer.trim()) {
                flushEventBlock(buffer);
            }

            if (results.length === sourceLeads.length) {
                return results;
            }

            const tail = buffer.trim();
            const fromJson = tryInterpretAsJsonEnrichmentBody(tail, sourceLeads);
            if (fromJson && fromJson.length === sourceLeads.length && results.length === 0) {
                return emitBatch(fromJson);
            }

            if (results.length < sourceLeads.length) {
                const err = new Error('Incomplete enrichment stream');
                err.partial = results;
                err.isIncomplete = true;
                throw err;
            }

            return results;
        } catch (error) {
            if (error?.name === 'AbortError' || controller.signal.aborted) {
                throw CANCELLED_REQUEST;
            }
            if (error?.partial) {
                throw error;
            }
            if (results.length > 0) {
                const err = error instanceof Error ? error : new Error(String(error));
                err.partial = results;
                throw err;
            }
            throw error;
        } finally {
            activeAbortControllers.delete(controller);
        }
    })();
}
